import { useState } from 'react'
import { Button, Input, Modal, Select, Textarea, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { useCreateCase, useLawyers } from '../hooks'
import { CASE_KINDS, kindLabel } from '../status'
import type { CaseDetail } from '../types'

export interface NewCaseModalProps {
  open: boolean
  onClose: () => void
  onCreated: (created: CaseDetail) => void
}

const EMPTY_FORM = {
  title: '',
  client_name: '',
  court_signature: '',
  court: '',
  court_short: '',
  case_kind: '',
  lead_id: '',
  description: '',
}

/**
 * Formularz „Nowa sprawa".
 *
 * Lista prowadzących pochodzi z `GET /api/cases/lawyers` (endpoint tego modułu) —
 * nie zależymy od panelu admina, który może być niewidoczny dla roli użytkownika.
 */
export function NewCaseModal({ open, onClose, onCreated }: NewCaseModalProps) {
  const [form, setForm] = useState(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)
  const lawyers = useLawyers(open)
  const createCase = useCreateCase()
  const toast = useToast()

  const set = (key: keyof typeof EMPTY_FORM) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))

  const close = () => {
    setForm(EMPTY_FORM)
    setError(null)
    onClose()
  }

  const submit = async () => {
    if (!form.title.trim() || !form.client_name.trim()) {
      setError('Tytuł sprawy i klient są wymagane.')
      return
    }
    setError(null)
    try {
      const created = await createCase.mutateAsync({
        title: form.title.trim(),
        client_name: form.client_name.trim(),
        court_signature: form.court_signature.trim() || null,
        court: form.court.trim() || null,
        court_short: form.court_short.trim() || form.court.trim() || null,
        case_kind: form.case_kind || null,
        lead_id: form.lead_id ? Number(form.lead_id) : null,
        description: form.description.trim() || null,
      })
      toast.success(`Założono sprawę ${created.internal_no}`)
      setForm(EMPTY_FORM)
      onCreated(created)
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się założyć sprawy.')
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Nowa sprawa"
      subtitle="Numer repertorium nadaje system automatycznie."
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Anuluj
          </Button>
          <Button variant="primary" loading={createCase.isPending} onClick={() => void submit()}>
            Załóż sprawę
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Input
          label="Tytuł sprawy"
          placeholder="Polbud S.A. przeciwko Termika Sp. z o.o. — o zapłatę"
          value={form.title}
          onChange={(event) => set('title')(event.target.value)}
        />
        <div className="grid grid-cols-2 gap-3.5">
          <Input
            label="Klient"
            placeholder="Polbud S.A."
            value={form.client_name}
            onChange={(event) => set('client_name')(event.target.value)}
          />
          <Input
            label="Sygnatura sądowa"
            mono
            placeholder="II C 1245/25"
            hint="Zostaw puste dla spraw przedsądowych"
            value={form.court_signature}
            onChange={(event) => set('court_signature')(event.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3.5">
          <Input
            label="Sąd"
            placeholder="Sąd Okręgowy w Warszawie, XXIV Wydział Cywilny"
            value={form.court}
            onChange={(event) => set('court')(event.target.value)}
          />
          <Input
            label="Sąd (skrót)"
            placeholder="SO Warszawa"
            value={form.court_short}
            onChange={(event) => set('court_short')(event.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3.5">
          <Select
            label="Rodzaj"
            placeholder="wybierz rodzaj"
            value={form.case_kind}
            onChange={(event) => set('case_kind')(event.target.value)}
            options={CASE_KINDS.map((value) => ({ value, label: kindLabel(value) }))}
          />
          <Select
            label="Prawnik prowadzący"
            placeholder={lawyers.isPending ? 'wczytywanie…' : 'ja'}
            value={form.lead_id}
            onChange={(event) => set('lead_id')(event.target.value)}
            options={(lawyers.data ?? []).map((lawyer) => ({
              value: String(lawyer.id),
              label: lawyer.title ? `${lawyer.full_name} — ${lawyer.title}` : lawyer.full_name,
            }))}
          />
        </div>
        <Textarea
          label="Opis"
          rows={3}
          placeholder="Krótki opis przedmiotu sprawy (opcjonalnie)"
          value={form.description}
          onChange={(event) => set('description')(event.target.value)}
        />
        {error ? <p className="text-micro text-danger">{error}</p> : null}
      </div>
    </Modal>
  )
}
